import type { ApiResponse, Brand } from '@fluenceur/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toaster } from '@/components/ui/toaster';
import type { UpdateBrandDetailsDto } from '@/services/brand-service';
import api from '@/libs/axiosInstance';
import type { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { billingContextQueryKey } from '@/hooks/queries/use-billing-context-query';
import { BillingTargetChangedError, useBillingActionScope } from './use-billing-action-scope';

interface UpdateBrandDetailsResponse {
  brand: Brand;
}

export const useUpdateBrandDetailsAction = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { scope, assertCurrent } = useBillingActionScope();

  return useMutation<
    UpdateBrandDetailsResponse,
    AxiosError<ApiResponse> | Error,
    Partial<UpdateBrandDetailsDto>,
    typeof scope
  >({
    retry: false,
    onMutate: () => {
      assertCurrent(scope);
      return scope;
    },
    mutationFn: async (payload): Promise<UpdateBrandDetailsResponse> => {
      assertCurrent(scope);
      const { data } = await api.put<unknown>('/dashboard/brands/me/details', payload);
      assertCurrent(scope);
      let value: unknown = data;
      if (data && typeof data === 'object' && 'success' in data) {
        const envelope = data as ApiResponse<unknown>;
        if (envelope.success !== true) throw new Error('Brand update failed');
        value = envelope.data;
      }
      if (
        !value ||
        typeof value !== 'object' ||
        Array.isArray(value) ||
        !('id' in value) ||
        value.id !== scope.brandId ||
        !('name' in value) ||
        typeof value.name !== 'string'
      ) {
        throw new Error('Invalid brand details response');
      }
      const brand = value as Brand;
      return { brand };
    },
    onSuccess: (response, _request, target) => {
      if (!target) return;
      try {
        assertCurrent(target);
      } catch {
        return;
      }
      // Invalidate and refetch brand details
      queryClient.invalidateQueries({ queryKey: ['brandDetails'] });

      // Update the cache with the new data
      queryClient.setQueryData(['brandDetails'], response.brand);
      void queryClient.invalidateQueries({
        queryKey: billingContextQueryKey(target),
        exact: true,
      });

      toaster.success({
        title: t('common.success'),
        description: t('settings.brandInformationForm.updateSuccess'),
      });
    },
    onError: (error) => {
      if (error instanceof BillingTargetChangedError) return;
      toaster.error({
        title: t('common.error'),
        description: t('settings.brandInformationForm.updateError'),
      });
    },
  });
};
